import React from 'react';
import { initials, pickAvatarGradient } from './utils';

function calcAge(dob, visitDateStr) {
  if (!dob) return null;
  const birth = new Date(dob);
  const ref = visitDateStr ? new Date(visitDateStr) : new Date();
  if (Number.isNaN(birth.getTime())) return null;
  let age = ref.getFullYear() - birth.getFullYear();
  const m = ref.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && ref.getDate() < birth.getDate())) age--;
  return age;
}

function formatDob(dob) {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return dob;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatVisitDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}

function primaryPhone(phoneNumbers) {
  if (!Array.isArray(phoneNumbers) || phoneNumbers.length === 0) return null;
  return (
    phoneNumbers.find((p) => p.is_primary) ||
    phoneNumbers[0]
  );
}

function Row({ label, value, href }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-slate-100 dark:border-slate-800/60 last:border-0">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 w-20 shrink-0 pt-0.5">
        {label}
      </span>
      {href ? (
        <a
          href={href}
          className="text-sm text-blue-600 dark:text-blue-400 hover:underline break-all"
        >
          {value}
        </a>
      ) : (
        <span className="text-sm text-slate-800 dark:text-slate-200 break-words">{value}</span>
      )}
    </div>
  );
}

export default function PatientProfileDrawer({ patient, isOpen, onClose }) {
  if (!isOpen || !patient) return null;

  const age = patient.age ?? calcAge(patient.dob, patient.date_);
  const sex = (patient.sex || patient.gender || '').toString();
  const sexLabel = sex.toLowerCase() === 'm' || sex.toLowerCase() === 'male' ? 'Male'
    : sex.toLowerCase() === 'f' || sex.toLowerCase() === 'female' ? 'Female'
    : sex || null;
  const mrn = patient.mrn || patient.EHR_ID || null;
  const phone = primaryPhone(patient.phone_numbers);
  const allPhones = Array.isArray(patient.phone_numbers) ? patient.phone_numbers : [];
  const visitType = (patient.type || '').toUpperCase();

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-30"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer — slides in from left, sits after the 56px icon rail */}
      <div
        role="dialog"
        aria-label="Patient profile"
        className="fixed top-0 left-14 bottom-0 z-40 w-80 bg-white dark:bg-[#0f0f17] shadow-2xl flex flex-col"
        style={{ borderRight: '1px solid rgba(148,163,184,0.2)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-800/60">
          <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Patient Profile</span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-7 h-7 rounded-md flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800/50 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-4 py-4">

          {/* Avatar + name */}
          <div className="flex items-center gap-3 mb-5">
            <div
              className={`w-14 h-14 rounded-2xl ${pickAvatarGradient(patient)} flex items-center justify-center text-white text-lg font-bold shadow-lg shrink-0`}
            >
              {initials(patient.patient_name)}
            </div>
            <div>
              <div className="font-bold text-base text-slate-900 dark:text-slate-100 leading-tight">
                {patient.patient_name || 'Unknown'}
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                {[age != null && `${age} yrs`, sexLabel, mrn && `MRN ${mrn}`].filter(Boolean).join(' · ')}
              </div>
            </div>
          </div>

          {/* Demographics */}
          <div className="mb-4">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-1">
              Demographics
            </div>
            <div className="bg-slate-50 dark:bg-slate-800/30 rounded-xl px-3">
              <Row label="DOB" value={formatDob(patient.dob)} />
              <Row label="Sex" value={sexLabel} />
              <Row label="MRN" value={mrn} />
            </div>
          </div>

          {/* Visit */}
          <div className="mb-4">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-1">
              Visit
            </div>
            <div className="bg-slate-50 dark:bg-slate-800/30 rounded-xl px-3">
              <Row label="Date" value={formatVisitDate(patient.date_)} />
              <Row label="Type" value={visitType || null} />
            </div>
          </div>

          {/* Contact */}
          <div className="mb-4">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-1">
              Contact
            </div>
            <div className="bg-slate-50 dark:bg-slate-800/30 rounded-xl px-3">
              <Row label="Email" value={patient.email} href={patient.email ? `mailto:${patient.email}` : null} />
              {allPhones.length > 0 ? (
                allPhones.map((p, i) => (
                  <Row
                    key={i}
                    label={p.phone_type || (i === 0 ? 'Phone' : `Phone ${i + 1}`)}
                    value={p.phone_number}
                    href={p.phone_number ? `tel:${p.phone_number}` : null}
                  />
                ))
              ) : (
                <Row label="Phone" value={patient.phone || patient.phone_number || null} />
              )}
            </div>
          </div>

          {/* Notes behavior badge if set */}
          {patient.notes_behavior && (
            <div className="mt-2">
              <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-full bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-500/20">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Notes: {patient.notes_behavior}
              </span>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
